import { useCallback, useEffect } from 'react';
import { useImmerAtom, useSetImmerAtom } from 'jotai-immer';
import { tblSpecLoadStorage, tblSpecLoadView } from '../../../../core/table/tableViewsRepo';
import { tableFiltersVM, tableStatsVM, tableViewVM } from '../../../../core/table/table_view_models';
import { MFR_RESTRICTIONS_LIST_STORAGE_KEY, MFR_RESTRICTIONS_LIST_VIEW_KEY } from '../../mfr_restrictions_const';
import {
    tableStateAtom,
    tableViewAtom,
    groupsDataAtom,
    tableStatsAtom,
    tableFiltersAtom,
    setAtomMFRRestrictionsListTableViewState,
} from '../mfr_restrictions_list_atoms';
import { mfrRestrictionsListColumnSpec, mfrRestrictionsListDefaultView } from './MFRRestrictionsTableSpec';
import viewStateRepo from '../../../../core/repo/viewStateRepo';
import { tableDataVM, tableStateVM } from '../../../../core/table/table_view_models';
import { MFRRestrictionsDataItem } from '../mfr_restrictions_list_types';
import { ListTableInitHookReturn, TableFilters } from '../../../../core/table/table.types';
import apiSvc from '../../../../core/services/apiSvc';
import { GET_LIST } from '../../data/mfrRestrictionsRepo';
import { mfrRestrictionsListApiToVM } from '../../data/mfrRestrictionsListApiToVM';

export const useMFRRestrictionsListTableInit = (): ListTableInitHookReturn => {
    const [{ init }, setState] = useImmerAtom(tableStateAtom);
    const setView = useSetImmerAtom(tableViewAtom);
    const setList = useSetImmerAtom(groupsDataAtom);
    const setStats = useSetImmerAtom(tableStatsAtom);
    const setFilter = useSetImmerAtom(tableFiltersAtom);

    const getListAsync = useCallback(async () => {
        try {
            const r = await apiSvc.getEntityList<MFRRestrictionsDataItem>(GET_LIST, {});
            setList(draft => {
                draft.dataItems = mfrRestrictionsListApiToVM(r);
                draft.items = [];
            });
        } catch (err: any) {
            // err msg handled upstream
            setList(() => tableDataVM<MFRRestrictionsDataItem>());
        } finally {
            setState(draft => {
                draft.waiting = false;
                draft.init = false;
                return draft;
            });
        }
    }, [setList, setState]);

    // INIT
    useEffect(() => {
        setState(draft => {
            if (draft.init && !draft.waiting) {
                // load custom views from storage
                const colSpec = mfrRestrictionsListColumnSpec();
                const defaultView = mfrRestrictionsListDefaultView();
                const viewStorage = tblSpecLoadStorage(MFR_RESTRICTIONS_LIST_VIEW_KEY, colSpec, defaultView);
                draft.views = viewStorage.views;
                draft.currentViewId = viewStorage.current;

                // set current view
                setView(() => tblSpecLoadView(viewStorage.current, viewStorage.views, colSpec) || tableViewVM());

                // viewstate filters and paging
                const viewstate = viewStateRepo.getTable(
                    MFR_RESTRICTIONS_LIST_STORAGE_KEY,
                    Array.from(colSpec.keys()),
                    defaultView.filterby,
                );

                if (viewstate?.sort) {
                    setView(draft => {
                        draft.sort = viewstate.sort;
                    });
                }
                if (viewstate?.paging) {
                    setStats(draft => {
                        draft.paging = viewstate.paging;
                    });
                }

                if (!viewstate?.flt && defaultView?.filterby) {
                    const presetFilters = defaultView.filterby.reduce((acc, f) => {
                        acc[f.key] = f.val;
                        return acc;
                    }, {} as TableFilters);
                    setFilter(() => presetFilters);
                } else if (viewstate?.flt) {
                    setFilter(() => viewstate.flt);
                }

                getListAsync();

                draft.waiting = true;
                return draft;
            } else {
                return draft;
            }
        });

        // destroy
        return (): void => {
            setAtomMFRRestrictionsListTableViewState();
            setState(() => tableStateVM());
            setView(() => tableViewVM());
            setFilter(() => tableFiltersVM());
            setStats(() => tableStatsVM());
            setList(() => tableDataVM<MFRRestrictionsDataItem>());
        };
    }, [setState, setView, getListAsync, setList, setFilter, setStats]);

    return { init, refetch: getListAsync };
};
